'use client';

import React, { useEffect, useState } from 'react';
// Prefetched on intent, not on sight (components/IntentLink.tsx).
import Link from '@/components/IntentLink';
import { MapPin, X } from 'lucide-react';
import { clearMyCity, useMyCity, type MyCity } from '@/lib/my-city';
import { placeHref, placeSlug } from '@/lib/place-url';

// The first button of the row of cities (owner's approval, 2026-09-29): the
// city this visitor last looked at, with a cross to forget it - or, before
// there is one, a suggestion from where the visitor seems to be
// (lib/visitor-place.ts), with a cross to say no. A suggestion is shown only
// for a city that has listings of this kind (`known`), so it never leads to an
// empty page. Nothing redirects or filters by itself.

const GUESS_OFF = 'rec_city_guess_off';
const GUESS_CACHE = 'rec_city_guess';

type Known = { city: string; province: string };

async function fetchGuess(): Promise<MyCity | null> {
  try {
    const cached = sessionStorage.getItem(GUESS_CACHE);
    if (cached) return JSON.parse(cached) as MyCity | null;
  } catch {
    // no session storage: ask again
  }
  try {
    const res = await fetch('/api/where', { cache: 'no-store' });
    const data = (await res.json()) as Partial<MyCity>;
    const guess = data.city && data.province ? { city: data.city, province: data.province } : null;
    try {
      sessionStorage.setItem(GUESS_CACHE, JSON.stringify(guess));
    } catch {
      // not cached
    }
    return guess;
  } catch {
    return null;
  }
}

export default function MyCityChip({ lang, deal, known = [] }: { lang: string; deal: 'sale' | 'rent'; known?: Known[] }) {
  const mine = useMyCity();
  const [guess, setGuess] = useState<MyCity | null>(null);
  const fr = lang === 'fr';

  useEffect(() => {
    if (mine) return;
    try {
      if (localStorage.getItem(GUESS_OFF)) return;
    } catch {
      // storage off: guess anyway
    }
    let alive = true;
    fetchGuess().then((g) => {
      if (!alive || !g) return;
      // The listed city it matches, in its own spelling.
      const match = known.find((k) => k.province === g.province && placeSlug(k.city) === placeSlug(g.city));
      if (match) setGuess({ city: match.city, province: match.province });
    });
    return () => {
      alive = false;
    };
    // `known` is a fresh array on every render of the server page; its content is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, JSON.stringify(known)]);

  const shown = mine ?? guess;
  if (!shown) return null;
  const suggestion = !mine;
  return (
    <span className={`shrink-0 inline-flex items-center rounded-xl border text-xs text-white ${suggestion ? 'border-dashed border-gold/60' : 'border-gold/60 bg-gold/10'}`}>
      <Link href={placeHref(lang, deal, shown)} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5">
        <MapPin aria-hidden="true" className="w-3.5 h-3.5 text-champagne" />
        <span className="sr-only">{suggestion ? (fr ? 'Près de chez vous ? ' : 'Near you? ') : fr ? 'Ma ville : ' : 'My city: '}</span>
        {shown.city}, {shown.province}
        {suggestion && <span aria-hidden="true" className="text-neutral-400">?</span>}
      </Link>
      <button
        type="button"
        onClick={() => {
          if (mine) return clearMyCity();
          try {
            localStorage.setItem(GUESS_OFF, '1');
          } catch {
            // storage off: hidden for this page
          }
          setGuess(null);
        }}
        aria-label={mine ? (fr ? `Oublier ${mine.city}` : `Forget ${mine.city}`) : fr ? 'Ne plus proposer une ville' : 'Stop suggesting a city'}
        className="pr-2.5 pl-1 py-1.5 text-neutral-300 hover:text-white cursor-pointer"
      >
        <X aria-hidden="true" className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}
